import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card, CardContent, CardHeader, CardActions, Button, Box, Divider,
  CircularProgress, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, TextField, IconButton, Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { fetchTemplate, deleteTemplate, fetchCanvasSizes, saveTemplate } from "../../../utils/truInkTemplate";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TemplatePreviewCanvas from "./TemplatePreviewCanvas";
import { useTheme, useMediaQuery } from "@mui/material";
import { ActionNotSupportedAlert } from "../../ActionNotSupportedAlert";



const gradientSx = {
  textTransform: "none",
  background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", opacity: 0.95, boxShadow: "none" },
  "&.Mui-disabled": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", opacity: 0.5, color: "#fff" },
};

const primarySx = {
  textTransform: "none",
  backgroundColor: "rgb(90, 28, 47)",
  "&:hover": { backgroundColor: "rgb(70, 20, 35)" },
  "&.Mui-disabled": { backgroundColor: "rgba(90, 28, 47, 0.4)", color: "#fff" },
};

function TemplateCard({ template, onEdit, onDuplicateOpen, onDelete ,isMobile ,setShowNotSupportedActionModal }) {
  return (
    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column", backgroundColor: "rgb(249, 249, 249) !important" }}>
      <CardHeader
        title={template.name}
        titleTypographyProps={{ noWrap: true, overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.9rem", fontWeight: 600 }}
      />
      <Divider />
      <CardContent sx={{ p: 0, flexGrow: 1 }}>
        <Box sx={{ width: "100%", height: 120, overflow: "hidden" }}>
          <TemplatePreviewCanvas elements={template.elements} resolution={template.resolution} />
        </Box>
      </CardContent>
      <Divider />
      <CardActions sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() =>{
              if(isMobile){
                setShowNotSupportedActionModal(true)
              }
              else{
                 onEdit(template.id)
              }
            }} sx={{ color: "rgb(90, 28, 47)" }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onDuplicateOpen(template)} sx={{ color: "rgb(90, 28, 47)" }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button size="small" onClick={() => onDelete(template.id)} sx={gradientSx}>
          Delete
        </Button>
      </CardActions>
    </Card>
  );
}


const AllTemplates = ({ templateSearchValue, setTemplateSearchValues }) => {
  const navigate = useNavigate();
  const selectedSite = useSelector((state) => state.truInk.selectedSite);
  const tenantId = selectedSite?.tenantId;
  const truInkState = useSelector((state) => state.truInk);
  const dispatch = useDispatch(); // ✅ single declaration

  const [templateData, setTemplateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTallMobile = useMediaQuery("(min-height:770px)");
  const [showNotSupportedActionModal, setShowNotSupportedActionModal] = useState(false)

  const TEMPLATES_PER_PAGE = useMemo(() => {
    if (isMobile) {
      return isTallMobile ? 2 : 1;
    }

    return 4;
  }, [isMobile, isTallMobile]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const fetchedTemplateData = await fetchTemplate(tenantId, null, selectedSite?.id);
      const fetchTemplateSizes = await fetchCanvasSizes(tenantId);
      const enrichedTemplates = (fetchedTemplateData || []).map((template) => {
        const matchingSize = fetchTemplateSizes.find((size) => size.id === template.canvasSizeId);
        let resolution = matchingSize ? matchingSize.resolution : "296x128";
        if (template.customProperties?.mode === "portrait") {
          const [w, h] = resolution.split("x").map(Number);
          resolution = `${h}x${w}`;
        }
        return { ...template, resolution };
      });
      setTemplateData(enrichedTemplates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!tenantId) return;
    loadTemplates();
  }, [tenantId]);

  useEffect(() => {
    const delay = setTimeout(() => setCurrentPage(1), 500);
    return () => clearTimeout(delay);
  }, [templateSearchValue]);

  useEffect(() => {
    return () => setTemplateSearchValues("");
  }, []);

  const handleEdit = (templateId) => {
    sessionStorage.setItem("currentState", JSON.stringify(truInkState));
    navigate(`/${templateId}`);
  };

  // ✅ receives id from TemplateCard's onDelete(template.id)
  const handleDeleteOpen = (templateId) => {
    setConfirmDeleteId(templateId);
  };

  const handleDelete = async () => {
    try {
      await deleteTemplate(confirmDeleteId, tenantId);
      setTemplateData((prev) => prev.filter((tpl) => tpl.id !== confirmDeleteId));
      toast.success("Template deleted successfully");
    } catch (err) {
      console.error("Error deleting template:", err);
      toast.error("Failed to delete template");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleDuplicateOpen = (template) => {
    setDuplicatingTemplate(template);
    setDuplicateName(`Copy of ${template.name}`);
    setDuplicateDialogOpen(true);
  };

  const closeDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicatingTemplate(null);
    setDuplicateName("");
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateName.trim() || !duplicatingTemplate) return;
    setDuplicating(true);
    try {
      const { id, resolution, ...rest } = duplicatingTemplate;
      await saveTemplate({ ...rest, name: duplicateName.trim() }, tenantId, selectedSite?.id);
      toast.success("Template duplicated successfully");
      await loadTemplates();
      closeDuplicateDialog();
    } catch (err) {
      console.error("Error duplicating template:", err);
      toast.error("Failed to duplicate template");
    } finally {
      setDuplicating(false);
    }
  };

  const filteredTemplates = templateSearchValue?.trim()
    ? templateData.filter((t) => t.name?.toLowerCase().includes(templateSearchValue.trim().toLowerCase()))
    : templateData;

  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * TEMPLATES_PER_PAGE,
    currentPage * TEMPLATES_PER_PAGE
  );

  const totalPages = Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE);

  return (
    <>
    <Box sx={{ width: '100%', zIndex: 5 }}>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : (

        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 2fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          width: "100%",
        }}>
          {
            (!paginatedTemplates || !paginatedTemplates?.length) ?
              <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center" }}>
                <h2
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    fontWeight: 300,
                    marginTop: "20px",
                  }}
                >
                  No results found.
                </h2>
              </div> :
               paginatedTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onDuplicateOpen={handleDuplicateOpen}
                onDelete={handleDeleteOpen} 
                isMobile={isMobile}
                setShowNotSupportedActionModal={setShowNotSupportedActionModal}
              />
            ))
          }
            </Box>

      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={totalPages} page={currentPage} onChange={(_, val) => setCurrentPage(val)} color="primary" />
        </Box>
      )}

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this template? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} sx={gradientSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDelete} sx={primarySx}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={closeDuplicateDialog} fullWidth maxWidth="xs">
        <DialogTitle>Duplicate Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Template Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleDuplicateConfirm(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDuplicateDialog} disabled={duplicating} sx={gradientSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDuplicateConfirm} disabled={!duplicateName.trim() || duplicating} sx={primarySx}>
            {duplicating ? <CircularProgress size={18} color="inherit" /> : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>



    <ActionNotSupportedAlert 
    open={showNotSupportedActionModal}
    setOpen={setShowNotSupportedActionModal}
    title = "Please use the desktop or android application for a better experience."
    isTruink={true}
    />
    </>
  );
};

export default AllTemplates;